import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
    CentralRegulatorDeactivationManagementControllerService,
    CentralRegulatorDTO,
    ScheduleCentralRegulatorDeactivationRequest,
    UpdateCentralRegulatorDeactivationDateRequest,
    CancelCentralRegulatorDeactivationRequest,
    RevertExecutedCentralRegulatorDeactivationRequest
} from '@iota/inh-core-v1-management-client';
import {
    CreateCentralRegulatorDeactivation,
    CreateCentralRegulatorDeactivationSuccess,
    CreateCentralRegulatorDeactivationFailure,
    UpdateCentralRegulatorDeactivation,
    UpdateCentralRegulatorDeactivationSuccess,
    UpdateCentralRegulatorDeactivationFailure,
    CancelCentralRegulatorDeactivation,
    CancelCentralRegulatorDeactivationSuccess,
    CancelCentralRegulatorDeactivationFailure,
    RevertCentralRegulatorDeactivation,
    RevertCentralRegulatorDeactivationSuccess,
    RevertCentralRegulatorDeactivationFailure
} from '../actions/central-regulator-deactivation.actions';

@Injectable()
export class CentralRegulatorDeactivationEffects {
    constructor(
        private actions$: Actions,
        private centralRegulatorDeactivationService: CentralRegulatorDeactivationManagementControllerService
    ) {}

    createDeactivation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CreateCentralRegulatorDeactivation),
            mergeMap((action) => {
                const request: ScheduleCentralRegulatorDeactivationRequest = {
                    centralRegulatorId: action.centralRegulatorId,
                    centralRegulatorDeactivationDate: action.deactivationDate
                };

                return this.centralRegulatorDeactivationService.scheduleCentralRegulatorDeactivation(request).pipe(
                    map((centralRegulator: CentralRegulatorDTO) => CreateCentralRegulatorDeactivationSuccess({ centralRegulator })),
                    catchError((error) =>
                        of(CreateCentralRegulatorDeactivationFailure({ error: error.message || 'Failed to create deactivation' }))
                    )
                );
            })
        )
    );

    updateDeactivation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdateCentralRegulatorDeactivation),
            mergeMap((action) => {
                const request: UpdateCentralRegulatorDeactivationDateRequest = {
                    centralRegulatorId: action.centralRegulatorId,
                    newCentralRegulatorDeactivationDate: action.deactivationDate
                };

                return this.centralRegulatorDeactivationService.updateCentralRegulatorDeactivationDate(request).pipe(
                    map((centralRegulator: CentralRegulatorDTO) => UpdateCentralRegulatorDeactivationSuccess({ centralRegulator })),
                    catchError((error) =>
                        of(UpdateCentralRegulatorDeactivationFailure({ error: error.message || 'Failed to update deactivation' }))
                    )
                );
            })
        )
    );

    cancelDeactivation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CancelCentralRegulatorDeactivation),
            mergeMap((action) => {
                const request: CancelCentralRegulatorDeactivationRequest = {
                    centralRegulatorId: action.centralRegulatorId
                };

                return this.centralRegulatorDeactivationService.cancelCentralRegulatorDeactivation(request).pipe(
                    map((centralRegulator: CentralRegulatorDTO) => CancelCentralRegulatorDeactivationSuccess({ centralRegulator })),
                    catchError((error) =>
                        of(CancelCentralRegulatorDeactivationFailure({ error: error.message || 'Failed to cancel deactivation' }))
                    )
                );
            })
        )
    );

    revertDeactivation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RevertCentralRegulatorDeactivation),
            mergeMap((action) => {
                const request: RevertExecutedCentralRegulatorDeactivationRequest = {
                    centralRegulatorId: action.centralRegulatorId
                };

                return this.centralRegulatorDeactivationService.revertExecutedCentralRegulatorDeactivation(request).pipe(
                    map((centralRegulator: CentralRegulatorDTO) => RevertCentralRegulatorDeactivationSuccess({ centralRegulator })),
                    catchError((error) =>
                        of(RevertCentralRegulatorDeactivationFailure({ error: error.message || 'Failed to revert deactivation' }))
                    )
                );
            })
        )
    );
}
